import { FormInput, SwitchInput, useFormContext } from "@4uhub/lib4uhub";
import { Alert, Grid, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { memo, useEffect } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BoardList } from "../../../BasicData/BoardList/BoardList";
import { TMessageForm } from "../messageSchema";
import { ITicketAutomation } from "../../../../../../../../models/ticket-automation-rules";
import { IRuleTemplate } from "../../../../../../../../store/contexts/AutomationRulesContext";

const translationPath = "components.messages.";

// Lembretes de agendamento/cirurgia que aceitam template sem botões
const REMINDER_AUTOMATION_CODES = ["16", "17", "18", "23"];
// Ação "Nenhuma" do template
const TEMPLATE_ACTION_NONE = "1";

export const Actions = memo(
  ({
    automationCode,
    automation,
    ruleTemplate,
  }: {
    automationCode: string | null;
    automation: ITicketAutomation;
    ruleTemplate?: IRuleTemplate | null;
  }) => {
    const { t } = useTranslation();
    const { control, setValue } = useFormContext<TMessageForm>();

    const moveTicket = useWatch({ name: "moveTicket", control });
    const moveTicketCancel = useWatch({ name: "moveTicketCancel", control });
    const moveTicketNotMe = useWatch({ name: "moveTicketNotMe", control });
    const moveTicketConfirm = useWatch({ name: "moveTicketConfirm", control });
    const moveTicketAttendant = useWatch({
      name: "moveTicketAttendant",
      control,
    });
    const confirmScheduleWithOk = useWatch({
      name: "confirmScheduleWithOk",
      control,
    });
    const cancelCodeConsult = useWatch({
      name: "cancellationReasonCode",
      control,
    });
    const cancelCodeExam = useWatch({
      name: "examCancellationReasonCode",
      control,
    });

    const cancelCodeSurgery = useWatch({
      name: "surgeryCancellationReasonCode",
      control,
    });

    // facilita: lista de códigos que vêm no automation.actionTypes
    const actionCodes = automation?.actionTypes?.map((a) => a.code) ?? [];

    // Lembrete com template sem botões: o ticket é finalizado após o envio,
    // então os campos que dependem da resposta do paciente não têm efeito
    const hideReplyActions =
      !!automationCode &&
      REMINDER_AUTOMATION_CODES.includes(String(automationCode)) &&
      ruleTemplate?.actionTypeCode === TEMPLATE_ACTION_NONE;

    useEffect(() => {
      if (!hideReplyActions) return;
      setValue("confirmScheduleWithOk", false);
      setValue("cancellationReasonCode", null);
      setValue("cancellationReasonDescription", null);
      setValue("examCancellationReasonCode", null);
      setValue("examCancellationReasonDescription", null);
      setValue("moveTicket", false);
      setValue("moveTicketCancel", false);
      setValue("moveTicketNotMe", false);
      setValue("moveTicketConfirm", false);
    }, [hideReplyActions, setValue]);

    useEffect(() => {
      if (!moveTicket) {
        setValue("ticketChannelWhenNot", null);
        setValue("ticketChannelStepWhenNot", null);
      }
      if (!moveTicketCancel) {
        setValue("ticketChannelWhenCancel", null);
        setValue("ticketChannelStepWhenCancel", null);
      }
      if (!moveTicketNotMe) {
        setValue("ticketChannelWhenNotMe", null);
        setValue("ticketChannelStepWhenNotMe", null);
      }
      if (!moveTicketConfirm) {
        setValue("ticketChannelWhenConfirm", null);
        setValue("ticketChannelStepWhenConfirm", null);
      }
      if (!moveTicketAttendant) {
        setValue("ticketChannelWhenAttendant", null);
        setValue("ticketChannelStepWhenAttendant", null);
      }
      if (
        !confirmScheduleWithOk &&
        automationCode &&
        ["16", "17", "18"].includes(String(automationCode))
      ) {
        setValue("moveTicketConfirm", false);
        setValue("ticketChannelWhenConfirm", null);
        setValue("ticketChannelStepWhenConfirm", null);
      }
    }, [
      setValue,
      moveTicket,
      moveTicketCancel,
      moveTicketNotMe,
      moveTicketConfirm,
      moveTicketAttendant,
      confirmScheduleWithOk,
    ]);

    return (
      <Grid
        container
        spacing={2}
        sx={(t) => ({
          mt: 5,
          pr: 3,
          pl: 1,
          py: 3,
          border: `1px solid ${t.palette.divider}`,
          borderRadius: 2,
        })}
      >
        <Grid item sm={12}>
          <Typography fontWeight="bold">
            {t(`${translationPath}actions`)}
          </Typography>
        </Grid>

        {hideReplyActions && (
          <Grid item xs={12} sm={12}>
            <Alert severity="info">
              {t(translationPath + "noReplyActionsWarning", {
                template: ruleTemplate?.name,
              })}
            </Alert>
          </Grid>
        )}

        {!hideReplyActions &&
          automationCode &&
          ["16", "17", "18"].includes(String(automationCode)) && (
            <>
              <Grid item xs={12} sm={12}>
                <SwitchInput
                  name="confirmScheduleWithOk"
                  label={t(translationPath + "confirmScheduleWithOk")}
                />
              </Grid>
            </>
          )}

        {!hideReplyActions &&
          automationCode &&
          ["1", "11", "12", "16", "18"].includes(String(automationCode)) && (
            <>
              <Grid
                item
                xs={12}
                sm={cancelCodeConsult?.includes(",") ? 12 : 4.5}
              >
                <FormInput
                  size="small"
                  name="cancellationReasonCode"
                  fullWidth
                  label={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>
                        {t(translationPath + "cancellationReasonCode") ||
                          "CancellationReasonCode"}
                      </span>
                      <Tooltip
                        title={
                          t(translationPath + "cancellationReasonHelpText") ||
                          "Enter the reason codes separated by commas. If only one is registered, it will be applied directly; if more than one is registered, the user will be able to choose which one to use."
                        }
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          color="action"
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </div>
                  }
                />
              </Grid>
              {!cancelCodeConsult?.includes(",") && (
                <Grid item xs={12} sm={7.5}>
                  <FormInput
                    size="small"
                    name="cancellationReasonDescription"
                    fullWidth
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span>
                          {t(
                            translationPath + "cancellationReasonDescription",
                          ) || "CancellationReasonDescription"}
                        </span>
                        <Tooltip
                          title={
                            t(
                              translationPath +
                                "cancellationReasonDescHelpText",
                            ) ||
                            "Enter an alternative description for the selected reason."
                          }
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            color="action"
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      </div>
                    }
                  />
                </Grid>
              )}
            </>
          )}

        {!hideReplyActions &&
          automationCode &&
          ["5", "11", "13", "17", "18"].includes(String(automationCode)) && (
            <>
              <Grid item xs={12} sm={cancelCodeExam?.includes(",") ? 12 : 4.5}>
                <FormInput
                  size="small"
                  name="examCancellationReasonCode"
                  fullWidth
                  label={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>
                        {t(translationPath + "examCancellationReasonCode") ||
                          "ExamCancellationReasonCode"}
                      </span>
                      <Tooltip
                        title={
                          t(
                            translationPath + "examCancellationReasonHelpText",
                          ) ||
                          "Enter the reason codes separated by commas. If only one is registered, it will be applied directly; if more than one is registered, the user will be able to choose which one to use."
                        }
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          color="action"
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </div>
                  }
                />
              </Grid>
              {!cancelCodeExam?.includes(",") && (
                <Grid item xs={12} sm={7.5}>
                  <FormInput
                    size="small"
                    name="examCancellationReasonDescription"
                    fullWidth
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span>
                          {t(
                            translationPath +
                              "examCancellationReasonDescription",
                          ) || "ExamCancellationReasonDescription"}
                        </span>
                        <Tooltip
                          title={
                            t(
                              translationPath +
                                "examCancellationReasonDescHelpText",
                            ) ||
                            "Enter an alternative description for the selected reason."
                          }
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            color="action"
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      </div>
                    }
                  />
                </Grid>
              )}
            </>
          )}

        {automationCode && ["34"].includes(String(automationCode)) && (
          <>
            <Grid item xs={12} sm={cancelCodeSurgery?.includes(",") ? 12 : 4.5}>
              <FormInput
                size="small"
                name="surgeryCancellationReasonCode"
                fullWidth
                label={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>
                      {t(translationPath + "surgeryCancellationReasonCode") ||
                        "SurgeryCancellationReasonCode"}
                    </span>
                    <Tooltip
                      title={
                        t(
                          translationPath + "surgeryCancellationReasonHelpText",
                        ) ||
                        "Enter the reason codes separated by commas. If only one is registered, it will be applied directly; if more than one is registered, the user will be able to choose which one to use."
                      }
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        fontSize="small"
                        color="action"
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
              />
            </Grid>
            {!cancelCodeSurgery?.includes(",") && (
              <Grid item xs={12} sm={7.5}>
                <FormInput
                  size="small"
                  name="surgeryCancellationReasonDescription"
                  fullWidth
                  label={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>
                        {t(
                          translationPath +
                            "surgeryCancellationReasonDescription",
                        ) || "SurgeryCancellationReasonDescription"}
                      </span>
                      <Tooltip
                        title={
                          t(
                            translationPath +
                              "surgeryCancellationReasonDescHelpText",
                          ) ||
                          "Enter an alternative description for the selected reason."
                        }
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          color="action"
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </div>
                  }
                />
              </Grid>
            )}
          </>
        )}

        {automationCode && ["22"].includes(String(automationCode)) && (
          <>
            <Grid item xs={12} sm={12}>
              <FormInput
                size="small"
                name="calendarHistoryType"
                fullWidth
                label={
                  t(translationPath + "calendarHistoryType") ||
                  "CalendarHistoryType"
                }
              />
            </Grid>
          </>
        )}

        {!hideReplyActions &&
          automationCode &&
          [
            "1",
            "4",
            "5",
            "9",
            "10",
            "11",
            "12",
            "13",
            "14",
            "16",
            "17",
            "18",
            "22",
            "23",
            "24",
            "34",
          ].includes(String(automationCode)) &&
          actionCodes.some((c) =>
            ["2", "3", "4", "5", "6", "7", "8", "10", "11"].includes(String(c)),
          ) && (
            <>
              <Grid item xs={12} sm={12}>
                <SwitchInput
                  name="moveTicket"
                  label={t(`${translationPath}move_ticket`)}
                />
              </Grid>
              {moveTicket && (
                <BoardList
                  boardFieldName="ticketChannelWhenNot"
                  listFieldName="ticketChannelStepWhenNot"
                />
              )}
            </>
          )}

        {!hideReplyActions &&
          automationCode &&
          [
            "1",
            "5",
            "11",
            "12",
            "13",
            "14",
            "16",
            "17",
            "18",
            "22",
            "34",
          ].includes(String(automationCode)) &&
          actionCodes.some((c) => ["3", "4", "6", "7"].includes(String(c))) && (
            <>
              <Grid item xs={12} sm={12}>
                <SwitchInput
                  name="moveTicketCancel"
                  label={t(`${translationPath}move_ticket_cancel`)}
                />
              </Grid>
              {moveTicketCancel && (
                <BoardList
                  boardFieldName="ticketChannelWhenCancel"
                  listFieldName="ticketChannelStepWhenCancel"
                />
              )}
            </>
          )}

        {!hideReplyActions &&
          automationCode &&
          [
            "1",
            "5",
            "11",
            "12",
            "13",
            "14",
            "16",
            "17",
            "18",
            "22",
            "34",
          ].includes(String(automationCode)) &&
          actionCodes.some((c) => ["4", "6", "8"].includes(String(c))) && (
            <>
              <Grid item xs={12} sm={12}>
                <SwitchInput
                  name="moveTicketNotMe"
                  label={t(`${translationPath}move_ticket_not_me`)}
                />
              </Grid>
              {moveTicketNotMe && (
                <BoardList
                  boardFieldName="ticketChannelWhenNotMe"
                  listFieldName="ticketChannelStepWhenNotMe"
                />
              )}
            </>
          )}

        {!hideReplyActions &&
          automationCode &&
          ((["1", "5", "11", "22", "34"].includes(String(automationCode)) &&
            actionCodes.some((c) => ["2", "6", "7"].includes(String(c)))) ||
            confirmScheduleWithOk) && (
            <>
              <Grid item xs={12} sm={12}>
                <SwitchInput
                  name="moveTicketConfirm"
                  label={t(`${translationPath}move_ticket_confirm`)}
                />
              </Grid>
              {moveTicketConfirm && (
                <BoardList
                  boardFieldName="ticketChannelWhenConfirm"
                  listFieldName="ticketChannelStepWhenConfirm"
                />
              )}
            </>
          )}
        {automationCode &&
          ["24"].includes(String(automationCode)) &&
          actionCodes.some((c) => ["11"].includes(String(c))) && (
            <>
              <Grid item xs={12} sm={12}>
                <SwitchInput
                  name="moveTicketAttendant"
                  label={t(`${translationPath}move_ticket_attendant`)}
                />
              </Grid>
              {moveTicketAttendant && (
                <BoardList
                  boardFieldName="ticketChannelWhenAttendant"
                  listFieldName="ticketChannelStepWhenAttendant"
                />
              )}
            </>
          )}
      </Grid>
    );
  },
);
